/* Small native components. No carousel, video-feed or modal dependency. */
(() => {
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  const canAutoplay = () => !reducedMotion.matches && !navigator.connection?.saveData && !['slow-2g', '2g'].includes(navigator.connection?.effectiveType);
  const play = video => { video.muted = true; video.play()?.catch(() => {}); };
  const pauseWithin = root => root.querySelectorAll('video').forEach(video => video.pause());

  class CTSnapGallery extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.track = this.querySelector('.ct-snap-gallery__track');
      this.slides = [...this.track.children]; this.index = 0;
      const signal = this.controller.signal;
      this.querySelector('[data-ct-prev]')?.addEventListener('click', () => this.go(this.index - 1), { signal });
      this.querySelector('[data-ct-next]')?.addEventListener('click', () => this.go(this.index + 1), { signal });
      this.track.addEventListener('keydown', event => {
        if (event.target !== this.track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault(); this.go(this.index + (event.key === 'ArrowRight' ? 1 : -1));
      }, { signal });
      this.observer = new IntersectionObserver(entries => {
        for (const entry of entries) {
          if (!entry.isIntersecting || entry.intersectionRatio < .6) { pauseWithin(entry.target); continue; }
          this.index = this.slides.indexOf(entry.target); this.update();
        }
      }, { root: this.track, threshold: .6 });
      this.slides.forEach(slide => this.observer.observe(slide)); this.update();
    }
    go(index) {
      index = Math.max(0, Math.min(this.slides.length - 1, index));
      this.track.scrollTo({ left: index * this.track.clientWidth, behavior: reducedMotion.matches ? 'instant' : 'smooth' });
    }
    selectMedia(id) { const index = this.slides.findIndex(slide => slide.dataset.ctMediaId === id); if (index >= 0) this.go(index); }
    update() {
      const count = this.querySelector('[data-ct-count]'); if (count) count.textContent = `${this.index + 1} / ${this.slides.length}`;
      const previous = this.querySelector('[data-ct-prev]'), next = this.querySelector('[data-ct-next]');
      if (previous) previous.disabled = this.index === 0;
      if (next) next.disabled = this.index === this.slides.length - 1;
    }
    disconnectedCallback() { this.observer?.disconnect(); this.controller?.abort(); this.controller = null; }
  }

  class CTStories extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.videos = [...this.querySelectorAll('.ct-story > video')];
      this.observer = new IntersectionObserver(entries => {
        for (const { target: video, isIntersecting, intersectionRatio } of entries) {
          video.dataset.visible = String(isIntersecting && intersectionRatio >= .65);
          this.sync(video);
        }
      }, { threshold: .65 });
      this.videos.forEach(video => this.observer.observe(video));
      const sync = () => this.videos.forEach(video => this.sync(video));
      document.addEventListener('visibilitychange', sync, { signal: this.controller.signal });
      reducedMotion.addEventListener('change', sync, { signal: this.controller.signal });
    }
    sync(video) {
      if (video.dataset.visible !== 'true' || document.hidden || !canAutoplay() || document.querySelector('.ct-modal[open]')) { video.pause(); return; }
      const source = video.querySelector('source[data-src]');
      if (source && !source.src) { source.src = source.dataset.src; video.load(); }
      play(video);
    }
    disconnectedCallback() { this.observer?.disconnect(); this.controller?.abort(); this.controller = null; pauseWithin(this); }
  }

  class CTCinematic extends HTMLElement {
    connectedCallback() {
      if (this.controller || !this.querySelector('template')) return;
      this.controller = new AbortController();
      this.toggle = this.querySelector('[data-ct-cinema-toggle]');
      this.observer = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
        if (this.visible && canAutoplay() && !this.userPaused && !document.hidden) this.start();
        else this.stop();
      }, { threshold: .2 });
      this.observer.observe(this);
      this.toggle.addEventListener('click', () => {
        this.userPaused = this.video ? !this.video.paused : false;
        if (this.userPaused) this.stop(); else this.start();
      }, { signal: this.controller.signal });
      const sync = () => { if (document.hidden || !canAutoplay()) this.stop(); else if (this.visible && !this.userPaused) this.start(); };
      document.addEventListener('visibilitychange', sync, { signal: this.controller.signal });
      reducedMotion.addEventListener('change', sync, { signal: this.controller.signal });
    }
    start() {
      if (!this.video) {
        const template = (matchMedia('(max-width:749px)').matches && this.querySelector('[data-ct-mobile-video]')) || this.querySelector('[data-ct-desktop-video]');
        this.video = template.content.querySelector('video').cloneNode(true);
        this.video.poster = this.querySelector('picture img').currentSrc;
        this.video.addEventListener('play', () => { this.toggle.textContent = 'Pause video'; this.toggle.setAttribute('aria-label', 'Pause campaign video'); });
        this.video.addEventListener('pause', () => { this.toggle.textContent = 'Play video'; this.toggle.setAttribute('aria-label', 'Play campaign video'); });
        this.append(this.video);
      }
      play(this.video);
    }
    stop() { this.video?.pause(); }
    disconnectedCallback() { this.stop(); this.observer?.disconnect(); this.controller?.abort(); this.controller = null; }
  }

  for (const [name, component] of [['ct-snap-gallery', CTSnapGallery], ['ct-stories', CTStories], ['ct-cinematic', CTCinematic]]) {
    if (!customElements.get(name)) customElements.define(name, component);
  }

  let quickRequest;
  function openDialog(dialog, trigger) {
    document.querySelectorAll('ct-stories').forEach(pauseWithin);
    dialog.ctTrigger = trigger; dialog.showModal();
  }
  function closeDialog(dialog) { dialog.close(); }
  document.querySelectorAll('.ct-modal').forEach(dialog => {
    dialog.addEventListener('close', () => {
      pauseWithin(dialog);
      if (dialog.id === 'ct-video-modal') dialog.querySelector('[data-ct-video-content]').replaceChildren();
      if (dialog.id === 'ct-quick-view') quickRequest?.abort();
      dialog.ctTrigger?.focus({ preventScroll: true });
      document.querySelectorAll('ct-stories').forEach(stories => stories.videos?.forEach(video => stories.sync(video)));
    });
    dialog.addEventListener('click', event => {
      if (event.target !== dialog) return;
      const rect = dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) closeDialog(dialog);
    });
  });

  async function quickView(trigger) {
    const dialog = document.getElementById('ct-quick-view');
    const content = dialog.querySelector('[data-ct-quick-content]');
    content.textContent = 'Loading product…'; content.setAttribute('aria-busy', 'true');
    openDialog(dialog, trigger);
    quickRequest?.abort();
    const request = new AbortController();
    quickRequest = request;
    try {
      const url = new URL(trigger.dataset.ctQuickView, location.origin);
      if (url.origin !== location.origin) throw new Error('Invalid product URL');
      url.searchParams.set('section_id', 'ct-quick-view');
      const response = await fetch(url, { signal: request.signal });
      if (!response.ok) throw new Error('Product unavailable');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      if (request.signal.aborted || quickRequest !== request || !dialog.open) return;
      const product = doc.querySelector('[data-ct-quick-product]');
      if (!product) throw new Error('Product unavailable');
      content.replaceChildren(product);
      const form = content.querySelector('form'), select = form.querySelector('select[name="id"]');
      const button = form.querySelector('[type="submit"]'), status = form.querySelector('[data-ct-quick-status]');
      let busy = false;
      const update = () => {
        const option = select.selectedOptions[0];
        const available = Boolean(select.value && !option.disabled);
        if (option.dataset.money) content.querySelector('[data-ct-quick-price]').textContent = option.dataset.money;
        content.querySelector('[data-ct-quick-availability]').textContent = available ? 'Available to order' : 'Choose an available size';
        if (option.dataset.mediaId) content.querySelector('ct-snap-gallery').selectMedia(option.dataset.mediaId);
      };
      select.addEventListener('change', update);
      if (select.value) update();
      form.addEventListener('submit', async event => {
        event.preventDefault();
        if (busy || !form.reportValidity() || !select.value || select.selectedOptions[0].disabled) return;
        busy = true; button.disabled = true; button.textContent = 'Adding…'; button.setAttribute('aria-busy', 'true');
        status.textContent = ''; status.dataset.error = 'false';
        try {
          status.textContent = await window.CTCart.add([{ id: Number(select.value), quantity: 1 }], trigger, () => closeDialog(dialog));
        } catch (error) { status.dataset.error = 'true'; status.textContent = error.message; }
        finally { busy = false; button.disabled = false; button.textContent = 'Add to bag'; button.removeAttribute('aria-busy'); }
      });
    } catch (error) {
      if (error.name !== 'AbortError' && !request.signal.aborted && quickRequest === request) {
        content.textContent = 'This product could not load. Please close this drawer and try again.';
      }
    } finally { if (quickRequest === request) content.removeAttribute('aria-busy'); }
  }
  document.addEventListener('click', event => {
    if (!(event.target instanceof Element)) return;
    const close = event.target.closest('[data-ct-close]');
    if (close) { closeDialog(close.closest('dialog')); return; }
    const quick = event.target.closest('[data-ct-quick-view]');
    if (quick) { event.preventDefault(); quickView(quick); return; }
    const tile = event.target.closest('[data-ct-video-open]');
    if (tile) {
      const dialog = document.getElementById('ct-video-modal');
      const video = tile.querySelector('template').content.querySelector('video').cloneNode(true);
      dialog.querySelector('[data-ct-video-content]').replaceChildren(video);
      openDialog(dialog, tile); play(video);
    }
  });
})();
